import React, { useCallback, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiUser, FiLogOut, FiSettings, FiMoon, FiSun, FiHeart, FiCreditCard } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import BrandSwitcher from './BrandSwitcher';
import OrganizerSetupBanner from './OrganizerSetupBanner';
import useModalFocusTrap from '../hooks/useModalFocusTrap';
import './Header.css';

const MOBILE_MENU_BACKGROUND = ['main', 'footer', '.organizer-setup-banner'];

function Header() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const menuButtonRef = useRef(null);
  const mobileNavRef = useRef(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useModalFocusTrap({
    active: menuOpen,
    containerRef: mobileNavRef,
    triggerRef: menuButtonRef,
    onClose: closeMenu,
    backgroundSelectors: MOBILE_MENU_BACKGROUND
  });

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setMenuOpen(false);
      setLoggingOut(false);
      navigate('/');
    }
  };

  const isAdmin = user && ['admin', 'organizer'].includes(user.role);
  const isAdminPage = location.pathname.startsWith('/admin');
  const sellTicketsPath = isAdmin ? '/admin/events' : '/become-organiser';

  // Mobile menu inline styles to avoid CSS cascade issues
  const mobileMenuStyle = {
    position: 'fixed',
    top: '70px',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
    zIndex: 9999,
    display: menuOpen ? 'flex' : 'none',
    flexDirection: 'column',
    overflowY: 'auto'
  };

  return (
    <>
      <header className="header">
        <div className="container header-container">
          <Link to="/" className="logo">
            <img
              src="/logo.png"
              srcSet="/logo.png 1x, /logo@2x.png 2x, /logo@3x.png 3x"
              alt="LoveVibesMusic"
              width="311"
              height="72"
              className="logo-image"
            />
          </Link>

          {isAdmin && isAdminPage && (
            <div className="header-brand-switcher">
              <BrandSwitcher />
            </div>
          )}

          {/* Desktop navigation */}
          <nav className="nav desktop-nav">
            <Link to="/events">Events</Link>
            <Link to={sellTicketsPath}>Sell Tickets</Link>
            <Link to="/about">About</Link>
            <Link to="/faq">FAQs</Link>
            <Link to="/contact">Contact</Link>
            {user ? (
              <>
                <Link to="/orders">My Tickets</Link>
                <Link to="/favourites"><FiHeart /> Saved</Link>
                {user.role === 'organizer' && (
                  <Link to="/organiser/finance">Money</Link>
                )}
                {['admin', 'organizer'].includes(user.role) && (
                  <Link to="/admin">Dashboard</Link>
                )}
                <button className="theme-toggle" onClick={toggleTheme} aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                  {isDark ? <FiSun /> : <FiMoon />}
                </button>
                <div className="user-menu">
                  <button className="user-btn">
                    <FiUser /> {user.firstName || 'Account'}
                  </button>
                  <div className="user-dropdown">
                    <Link to="/payment-plans"><FiCreditCard /> Payment Plans</Link>
                    <Link to="/profile"><FiSettings /> Profile</Link>
                    <button onClick={handleLogout} disabled={loggingOut}>
                      <FiLogOut /> {loggingOut ? 'Logging out…' : 'Logout'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <button className="theme-toggle" onClick={toggleTheme} aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                  {isDark ? <FiSun /> : <FiMoon />}
                </button>
                <Link to="/login">Login</Link>
                <Link to="/register" className="btn btn-primary btn-sm">Sign Up</Link>
              </>
            )}
          </nav>

          <button
            ref={menuButtonRef}
            type="button"
            className="menu-toggle"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-controls="mobile-navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
          </button>
        </div>
      </header>

      <OrganizerSetupBanner />

      {/* Mobile navigation - outside header to avoid stacking context issues */}
      <nav
        ref={mobileNavRef}
        id="mobile-navigation"
        className="mobile-nav"
        style={mobileMenuStyle}
        aria-label="Mobile navigation"
        aria-hidden={!menuOpen}
        tabIndex={menuOpen ? -1 : undefined}
      >
        <Link to="/events" onClick={() => setMenuOpen(false)}>Events</Link>
        <Link to={sellTicketsPath} onClick={() => setMenuOpen(false)}>Sell Tickets</Link>
        <Link to="/about" onClick={() => setMenuOpen(false)}>About</Link>
        <Link to="/faq" onClick={() => setMenuOpen(false)}>FAQs</Link>
        <Link to="/contact" onClick={() => setMenuOpen(false)}>Contact</Link>
        <div className="mobile-nav-section-title">Legal</div>
        <Link to="/terms" onClick={() => setMenuOpen(false)}>Terms</Link>
        <Link to="/privacy" onClick={() => setMenuOpen(false)}>Privacy</Link>
        {user ? (
          <>
            <Link to="/orders" onClick={() => setMenuOpen(false)}>My Tickets</Link>
            <Link to="/payment-plans" onClick={() => setMenuOpen(false)}><FiCreditCard /> Payment Plans</Link>
            <Link to="/favourites" onClick={() => setMenuOpen(false)}><FiHeart /> Saved Events</Link>
            {user.role === 'organizer' && (
              <Link to="/organiser/finance" onClick={() => setMenuOpen(false)}>Money</Link>
            )}
            {['admin', 'organizer'].includes(user.role) && (
              <Link to="/admin" onClick={() => setMenuOpen(false)}>Dashboard</Link>
            )}
            <Link to="/profile" onClick={() => setMenuOpen(false)}>Profile</Link>
            <button className="mobile-theme-btn" onClick={toggleTheme}>
              {isDark ? <FiSun /> : <FiMoon />} {isDark ? 'Light Mode' : 'Dark Mode'}
            </button>
            <button className="mobile-logout-btn" onClick={handleLogout} disabled={loggingOut}>
              <FiLogOut /> {loggingOut ? 'Logging out…' : 'Logout'}
            </button>
          </>
        ) : (
          <>
            <button className="mobile-theme-btn" onClick={toggleTheme}>
              {isDark ? <FiSun /> : <FiMoon />} {isDark ? 'Light Mode' : 'Dark Mode'}
            </button>
            <Link to="/login" onClick={() => setMenuOpen(false)}>Login</Link>
            <Link to="/register" onClick={() => setMenuOpen(false)}>Sign Up</Link>
          </>
        )}
      </nav>
    </>
  );
}

export default Header;
